/**
 * Email generator utility functions
 * These functions take form data and generate properly formatted email content
 * for interview requests and quote check emails
 */

/**
 * Generates an interview request email based on provided form data
 * Creates a professional email asking for an interview for a Paw Print article
 *
 * @param {Object} data - Form data containing interview request information
 * @param {string} data.studentName - Name of the student requesting the interview
 * @param {string} data.studentYear - Year of the student (freshman, sophomore, junior, senior)
 * @param {string} data.sourceName - Name of the person being interviewed
 * @param {string} data.articleTopic - Topic of the article being written
 * @param {string} data.focusPoints - Main focus points of the article
 * @param {string} data.sourceConnection - Why this source is relevant to the topic
 * @param {string} data.interviewLength - Suggested length for the interview
 * @param {string} data.availability - Student's availability for the interview
 * @returns {Object} Object containing email subject and body
 */
export const generateInterviewEmail = (data) => {
  // Destructure the data object to get individual properties
  const {
    studentName,
    studentYear,
    studentTitle,
    studentPronouns,
    sourceName,
    articleTopic,
    focusPoints,
    sourceConnection,
    interviewLength,
    availability,
    addContext,
  } = data;

  // Create email subject line with the article topic
  const subject = `${articleTopic} interview for The Paw Print`;

  // Format focus points with proper grammar
  let formattedFocusPoints = focusPoints;
  if (focusPoints) {
    const points = focusPoints
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p);
    if (points.length === 2) {
      formattedFocusPoints = `${points[0]} and ${points[1]}`;
    } else if (points.length > 2) {
      const lastPoint = points.pop();
      formattedFocusPoints = `${points.join(", ")}, and ${lastPoint}`;
    } else {
      formattedFocusPoints = points[0] || "";
    }
  }

  // Create the student introduction line
  const schoolInfo = addContext ? " at Woodside High School" : "";
  const pawPrintInfo = addContext
    ? " our student publication,"
    : " our school newspaper,";

  // Create email body using the provided template
  let body = `Dear ${sourceName},

Hi, my name is ${studentName} and I am a ${studentYear}${schoolInfo}. I'm a part of${pawPrintInfo} the Paw Print, and I am writing an article about ${articleTopic}. I heard you are connected to ${articleTopic} because ${sourceConnection} so I thought you would be a good person to talk to. In this article, I want to explore ${formattedFocusPoints}.

Would you be open to a ${interviewLength} interview? If this sounds like something we could do, the best times for me to meet you would be ${availability}. You would help support student journalism here at Woodside.

Let me know what works for you!

Thank you,
${studentName}`;

  // Add optional title if provided
  if (studentTitle && studentTitle.trim()) {
    body += `\n${studentTitle}`;
  }

  // Add optional pronouns if provided
  if (studentPronouns && studentPronouns.trim()) {
    body += `\n${studentPronouns}`;
  }

  // Return both subject and body for use in the application
  return { subject, body };
};

/**
 * Generates a quote check email based on provided form data
 * Creates an email to send quotes back to a source for accuracy confirmation
 * This is a standard journalistic practice to ensure quotes are used correctly
 *
 * @param {Object} data - Form data containing quote check information
 * @param {string} data.qcStudentName - Name of the student sending the quote check
 * @param {string} data.qcSourceName - Name of the source who provided the quotes
 * @param {Array} data.quotes - Array of quote objects containing quote text
 * @returns {Object} Object containing email subject and body
 */
export const generateQuoteCheckEmail = (data) => {
  // Destructure the data object to get individual properties
  const { qcStudentName, qcSourceName, quotes } = data;

  // Create email subject line
  const subject = `Quote Check: Paw Print Article`;

  // Start building the email body with greeting and introduction
  let body = `Hi ${qcSourceName},

Thanks again for doing the interview for my story. Here are the quotes I plan to use for it:

`;

  // Add each quote to the email body with proper formatting
  quotes.forEach((quote) => {
    // Only include quotes that have actual text content
    if (quote.text.trim()) {
      if (quote.context && quote.context.trim()) {
        body += `- ${quote.context.trim()}, you said: "${quote.text.trim()}" - Does that sound right?\n\n`;
      } else {
        body += `- "${quote.text.trim()}" - Does that sound right?\n\n`;
      }
    }
  });

  // Add the standard quote check policy explanation and closing
  body += `Our policy dictates that we confirm quotes and their context for accuracy; this email is not intended as a request for revision. Thank you for your understanding, and for your help with my story.

Sincerely,
${qcStudentName}`;

  // Return both subject and body for use in the application
  return { subject, body };
};
